import { dateToFormattedFullDate } from "../../utils/dateAndTime";
import { convertFileToBase64 } from "../../utils/getBase64";

export const mapSalesFormToData = async (
    salesContracts,
    projectFinancialLegal,
    workingConditions,
    organizationId,
    date,
    confirmSops,
    isRfq,
    projectName,
    projectType,
    qaDetails,
    operationalAttachments,
    targetPriceAttachments,
    deductionsValues
) => {
    const workingConditionsData = workingConditions.map((item) => ({
        truckTypeId: item.truckType.value,
        pickupLocationId: item.pickupLocation.value,
        pickupTime: item.pickupArrivalTime,
        numberOfTrucks: item.numberOfTruck,
        hasPreferredModelYear: item.hasPreferredModelYear,
        requiredModelYearStartsFrom: item.requiredModelYearStartsFrom?.value,
        numberOfHelpers: item.numberOfHelpers,
        deliveryDelegate: item.delegateWage,
        workingDays: item.workingDaysPerMonth,
        kickoffDate: dateToFormattedFullDate(item.KickOffDate),
        endDate: dateToFormattedFullDate(item.endDate),
        workingHoursPerDay: item.workingHoursPerDay,
        extraUnitDistancePrice: item.extraKiloMeterPrice,
        truckPricePerMonth: item.truckPricePerMonth,
        pricePerHelper: item.helperWage,
        pricePerDelegate: item.delegateWage,
        extraDayPrice: item.extraDayPrice,
        extraHourPrice: item.extraHoursPrice,
        overnightPrice: item.overNightPrice,
        truckDailyPrice: item.truckDailyPrice,
        distance: item.kiloMeters,
        oilPricePerKilometer: item.oilPricePerKilometer,
        fuelPricePerKilometer: item.fuelPricePerKilometer,
        offDays: item.weeklyOffDays.map((item) => item.value),
        serviceAreas: item.workingArea.map((item) => item.value),
        salesChannels: item.salesChannels.map((item) => item.value),
        operationalSpecialDetails: item.operationalDetailsSpecialRequired,
        facilitatorsSpecialDetails: item.facilitiesSpecialRequired,
        trucksSpecialDetails: item.truckDetailsSpecialRequired,
        excludeGasAndOil: item.excludeGasAndOil,
        goodsTransfer: item.goodsTransfer,
        cashCollection: item.cashCollection,
        distribution: item.distribution,
        scales: item.scales,
        tolls: item.tolls,
        returnable: item.returnable,
        workingOvernight: item.workingOvernight,
        workingOnHolidays: item.workingOnHolidays,
        specialExtraOperationalRequirments: item.specialExtraOperationalRequirments,
        maximumNumberOfDropOff: item.maximumNumberOfDropOff,
    }));

    const attachments = [];
    for (const document of operationalAttachments) {
        const base64File = await convertFileToBase64(document);
        attachments.push({
            document: base64File,
            documentType: "operational",
        });
    }
    for (const document of salesContracts) {
        const base64File = await convertFileToBase64(document);
        attachments.push({
            document: base64File,
            documentType: "sales_contract",
        });
    }
    for (const document of targetPriceAttachments) {
        const base64File = await convertFileToBase64(document);
        attachments.push({
            document: base64File,
            documentType: "target_price_document",
        });
    }

    return {
        data: {
            type: "projects",
            attributes: {
                name: projectName,
                organizationId,
                projectType: +projectType?.value,
                requestedAt: date,
                termsOfService: confirmSops,
                projectTruckTypes: workingConditionsData,
                isRequestForQuotation: isRfq,
                clientCollectionCycleDays: projectFinancialLegal?.ClientCollection,
                invoicingCycle: projectFinancialLegal?.invoicing,
                penaltiesAndDeductions: mapDeductionsToData(deductionsValues),
                specialPenaltiesAndDeductions: projectFinancialLegal?.specialPenaltiesAndDeductions,
                hasDeductionsPenalties: projectFinancialLegal?.hasDeductions,
                podsCollectionCycleDays: projectFinancialLegal?.PODs,
                ...(attachments?.length > 0 && { attachments: [...attachments] }),
                neededDocumentsAndPods: qaDetails.neededDocumentsAndPods,
                standardsAndOperationalProcedures: qaDetails.standardsAndOperationalProcedures.map(
                    (item) => ({ standardsAndOperationalProcedureId: +item })
                ),
                requiresIllaOperatorsInClientPremises:
                    qaDetails.requiresIllaOperatorsInClientPremises,
                requireDocumentsCollection: qaDetails.requireDocumentsCollection,
                hasPodDocument: qaDetails?.hasPodDocument,
                hasGrnDocument: qaDetails?.hasGrnDocument,
                specialStandardsAndOperationalProcedures:
                    qaDetails?.specialStandardsAndOperationalProcedures,
            },
        },
    };
};

export const mapUpdateSalesProjectInfoToData = (id, data, penaltiesAndDeductions, isRfq) => ({
    data: {
        type: "projects",
        id,
        attributes: {
            requireDocumentsCollection: data.qaDetails.requireDocumentsCollection,
            standardsAndOperationalProcedures: data.qaDetails.standardsAndOperationalProcedures.map(
                (item) => ({ standardsAndOperationalProcedureId: item })
            ),
            isRequestForQuotation: isRfq,
            clientCollectionCycleDays: data.projectFinancialLegal?.ClientCollection,
            invoicingCycle: data.projectFinancialLegal?.invoicing,
            penaltiesAndDeductions: mapDeductionsToData(penaltiesAndDeductions),
            specialPenaltiesAndDeductions:
                data.projectFinancialLegal?.specialPenaltiesAndDeductions,
            podsCollectionCycleDays: data.projectFinancialLegal?.PODs,
            name: data.projectName,
            requiresIllaOperatorsInClientPremises:
                data.qaDetails.requiresIllaOperatorsInClientPremises,
            hasPodDocument: data?.qaDetails?.hasPodDocument,
            hasGrnDocument: data?.qaDetails?.hasGrnDocument,
            specialStandardsAndOperationalProcedures:
                data?.qaDetails?.specialStandardsAndOperationalProcedures,
        },
    },
});

export const mapUpdateSupplierProjectInfoToData = (id, data) => ({
    data: {
        type: "projects",
        id,
        attributes: {
            supplyPaymentCycleDays: data.projectFinancialLegal?.supplyPaymentCycleDays,
            supplyReviewCycle: data.projectFinancialLegal?.supplyReviewCycle,
        },
    },
});

export const mapUpdateSupplyChainProjectInfoToData = (id, data) => ({
    data: {
        type: "projects",
        id,
        attributes: {
            desiredContributionMargin: data.projectFinancialLegal?.desiredMargin,
            clientCollectionCycleDays: data.projectFinancialLegal?.ClientCollection,
            invoicingCycle: data.projectFinancialLegal?.invoicing,
            podsCollectionCycleDays: data.projectFinancialLegal?.PODs,
            penaltiesAndDeductions: data.projectFinancialLegal?.deductions,
            legalTerms: data.projectFinancialLegal?.legal,
            supplyPaymentCycleDays: data.projectFinancialLegal?.supplyPaymentCycleDays,
            supplyReviewCycle: data.projectFinancialLegal?.supplyReviewCycle,
            neededDocumentsAndPods: data.qaDetails.neededDocumentsAndPods,
            operationsSiteVisitDetails: data.qaDetails.operationsSiteVisitDetails,
        },
    },
});

export const mapUpdateOperationsProjectInfoToData = (id, data) => ({
    data: {
        type: "projects",
        id,
        attributes: {
            operationsSiteVisitDetails: data.qaDetails.operationsSiteVisitDetails,
        },
    },
});
export const mapUpdatePricingDetailsToData = (workingConditionId, data) => ({
    data: {
        id: workingConditionId,
        type: "monthly-project-truck-type-conditions",
        attributes: {
            truckDailyPrice: data.truckDailyPrice,
            oilPricePerKilometer: data.oilPricePerKilometer,
            fuelPricePerKilometer: data.fuelPricePerKilometer,
        },
    },
});
export const mapUpdateSalesFormToData = (id, data) => ({
    data: {
        id,
        type: "monthlyProjectSalesConditions",
        attributes: {
            truckTypeId: data.truckType?.value,
            serviceAreas: data.serviceAreas.map((item) => item.value),
            offDays: data.weeklyOffDays.map((item) => item.value),
            operationalSpecialDetails: data.operationalDetailsCheckbox
                ? data.operationalSpecialDetails
                : "",
            facilitatorsSpecialDetails: data.facilitiesCheckbox
                ? data.facilitatorsSpecialDetails
                : "",
            distance: data.kiloMeters,
            hasPreferredModelYear: data.hasPreferredModelYear,
            requiredModelYearStartsFrom: data.requiredModelYearStartsFrom?.value,
            workingHoursPerDay: data.workingHoursPerDay,
            salesChannels: data.salesChannels.map((item) => item.value),
            numberOfTrucks: data.numberOfTrucks,
            numberOfHelpers: data.numberOfHelpers,
            deliveryDelegate: data.deliveryDelegate,
            workingDays: data.workingDaysPerMonth,
            kickoffDate: dateToFormattedFullDate(data.kickoffDate),
            endDate: dateToFormattedFullDate(data.endDate),
            overnightPrice: data.overnightPrice,
            truckDailyPrice: data.truckDailyPrice,
            pricePerHelper: data.pricePerHelper,
            pricePerDelegate: data.pricePerDelegate,
            extraDayPrice: data.extraDayPrice,
            extraHourPrice: data.extraHourPrice,
            truckPricePerMonth: data.truckPricePerMonth,
            extraUnitDistancePrice: data.extraUnitDistancePrice,
            pickupLocationId: data.pickUpLocation?.value,
            pickupTime: data.pickUpTime,
            oilPricePerKilometer: data.oilPricePerKilometer,
            fuelPricePerKilometer: data.fuelPricePerKilometer,
            excludeGasAndOil: data.excludeGasAndOil,
            goodsTransfer: data.goodsTransfer,
            cashCollection: data.cashCollection,
            distribution: data.distribution,
            scales: data.scales,
            tolls: data.tolls,
            returnable: data.returnable,
            workingOvernight: data.workingOvernight,
            workingOnHolidays: data.workingOnHolidays,
            specialExtraOperationalRequirments: data.specialExtraOperationalRequirments,
            maximumNumberOfDropOff: data.maximumNumberOfDropOff,
        },
    },
});
export const mapAddWorkingConditionFormToData = (projectId, data) => ({
    data: {
        type: "monthlyProjectSalesConditions",
        attributes: {
            monthlyProjectId: projectId,
            truckTypeId: data.truckType?.value,
            serviceAreas: data.serviceAreas.map((item) => item.value),
            offDays: data.weeklyOffDays.map((item) => item.value),
            operationalSpecialDetails: data.operationalDetailsCheckbox
                ? data.operationalSpecialDetails
                : "",
            facilitatorsSpecialDetails: data.facilitiesCheckbox
                ? data.facilitatorsSpecialDetails
                : "",
            distance: data.kiloMeters,
            hasPreferredModelYear: data.hasPreferredModelYear,
            requiredModelYearStartsFrom: data.requiredModelYearStartsFrom?.value,
            workingHoursPerDay: data.workingHoursPerDay,
            salesChannels: data.salesChannels.map((item) => item.value),
            workingDays: data.workingDaysPerMonth,
            numberOfTrucks: data.numberOfTrucks,
            numberOfHelpers: data.numberOfHelpers,
            deliveryDelegate: data.deliveryDelegate,
            kickoffDate: dateToFormattedFullDate(data.kickoffDate),
            endDate: dateToFormattedFullDate(data.endDate),
            overnightPrice: data.overnightPrice,
            pricePerHelper: data.pricePerHelper,
            pricePerDelegate: data.pricePerDelegate,
            extraDayPrice: data.extraDayPrice,
            extraHourPrice: data.extraHourPrice,
            truckPricePerMonth: data.truckPricePerMonth,
            extraUnitDistancePrice: data.extraUnitDistancePrice,
            pickupLocationId: data.pickUpLocation?.value,
            pickupTime: data.pickUpTime,
            // gasAndOilPerUnitDistance: data.gasAndOilPerUnitDistance,
            oilPricePerKilometer: data.oilPricePerKilometer,
            fuelPricePerKilometer: data.fuelPricePerKilometer,
            excludeGasAndOil: data.excludeGasAndOil,
            goodsTransfer: data.goodsTransfer,
            cashCollection: data.cashCollection,
            distribution: data.distribution,
            scales: data.scales,
            tolls: data.tolls,
            returnable: data.returnable,
            workingOvernight: data.workingOvernight,
            workingOnHolidays: data.workingOnHolidays,
            specialExtraOperationalRequirments: data.specialExtraOperationalRequirments,
            maximumNumberOfDropOff: data.maximumNumberOfDropOff,
        },
    },
});
export const mapSupplierFormToData = (workingConditionId, formData, suppliers) => ({
    data: {
        id: workingConditionId,
        type: "monthlyProjectSupplierConditions",
        attributes: {
            supplierTruckPricePerMonth: formData?.pricingDetails?.truckPricePerMonth,
            supplierExtraDayPrice: formData?.pricingDetails?.extraDayPrice,
            supplierExtraWorkingHourPrice: formData?.pricingDetails?.extraHourPrice,
            supplierOvernightPrice: formData?.pricingDetails?.overnightPrice,
            supplierPricePerDelegate: formData?.pricingDetails?.pricePerDelegate,
            supplierDeliveryDelegate: formData?.fullFillmentDetails?.deliveryDelegate,
            supplierNumberOfHelpers: formData?.fullFillmentDetails?.numberOfHelpers,
            supplierPricePerHelper: formData?.pricingDetails.pricePerHelper,
            supplierOtherDetails: formData?.pricingDetails?.otherDetails,
            supplierExcludeGasAndOil: formData?.pricingDetails?.excludeGasAndOil,
            supplierGasAndOilPerUnitDistance: formData?.pricingDetails?.GazPricePerkiloMeter,
            supplierExtraUnitDistancePrice: formData?.pricingDetails?.extraUnitDistancePrice,
            projectMonthlySuppliers:
                suppliers &&
                Object.values(suppliers)?.map((supplier) => ({
                    supplierId: supplier?.supplier?.value,
                    numberOfTrucks: supplier?.numberOfTrucks,
                    assignedTrucks: Object.values(supplier?.assignedTrucks)?.map((truck) => ({
                        truckId: truck?.truck?.value,
                        driverId: truck?.driver?.value,
                    })),
                    distance: supplier?.kiloMeters,
                    workingHoursPerDay: supplier?.workingHoursPerDay,
                })),

            supplierTermsOfService: formData?.pricingDetails?.confirmSops,
        },
    },
});

export const mapSuppliersTrucksToData = (suppliers) =>
    suppliers?.map((supplier) => ({
        assignedTrucks: supplier?.assignedTrucks?.map((truck) => ({
            truck: truck?.truck?.value,
            driver: truck?.driver?.value,
        })),
        kiloMeters: supplier?.kiloMeters,
        numberOfTrucks: supplier?.numberOfTrucks,
        supplierId: supplier?.supplier?.value,
        workingHoursPerDay: supplier?.workingHoursPerDay,
    }));

export const mapSupplyChainDeactivateReasonToData = (deactivationReason, conditionId) => ({
    data: {
        id: conditionId,
        type: "monthlyProjectSalesConditions",
        attributes: {
            deactivationReason: deactivationReason?.reason,
        },
    },
});
export const mapSalesDeactivateReasonToData = (deactivationReason, conditionId) => ({
    data: {
        id: conditionId,
        type: "monthlyProjectSalesConditions",
        attributes: {
            deactivationReason: deactivationReason?.reason,
            deactivationStatus: deactivationReason?.status,
        },
    },
});
export const mapDeclineWorkingConditionReasonToData = (declineReason, conditionId) => ({
    data: {
        id: conditionId,
        type: "monthly-project-truck-type-conditions",
        attributes: {
            declinationDetails: declineReason,
        },
    },
});
export const mapObjectOnWorkingConditionToData = (objections, conditionId) => ({
    data: {
        type: "objections",
        attributes: {
            objectionableId: conditionId,
            objectionableType: "Project::TruckTypes::Monthly",
            description: objections?.objectionComment,
            sectionId: objections?.section?.value,
            itemId: objections?.item?.value,
        },
    },
});

export const mapObjectionCommentToData = (id, comment) => ({
    data: {
        type: "comments",
        attributes: {
            content: comment,
            commentableType: "Objection", // this is should be the name of the entity you're commenting on
            commentableId: id, // this is should be the ID of the record you're commenting on
        },
    },
});
export const mapToUpdateProject = (id, data, attachments) => ({
    data: {
        type: "projects",
        id,
        attributes: {
            supplyPaymentCycleDays: data?.cycleDays,
            supplyReviewCycle: data?.reconciliation,
            ...(attachments?.length > 0 && { attachments: [...attachments] }),
        },
    },
});
export const mapToUpdateProjectAttachments = (attachments, documentType) => ({
    data: {
        type: "attachments",
        attributes: {
            attachedItemType: "Project",
            documentType,
            ...(attachments?.length > 0 && { document: [...attachments] }),
        },
    },
});

const mapDeductionsToData = (options) => {
    let mappedOptions = [];
    mappedOptions = Object.keys(options).map((optionId) => ({
        penaltiesAndDeductionId: parseInt(optionId),
        comment: options[optionId]?.comment || "",
    }));
    return mappedOptions;
};
